/**
 * One condition: a field, a way of comparing it, and a value.
 *
 * Shared by the two places that edit conditions, which are the same shape
 * asked in two different rooms. A view's filters decide which rows it holds; a
 * form question's conditions decide whether it is asked at all -- and both are
 * `field op value` combined by one connective, so both are this row.
 *
 * Three decisions live here rather than in either caller:
 *
 * - A condition naming a field the list does not hold is left unreadable
 *   rather than guessed at, with the way out still offered.
 * - The stored condition is always an option, even when it would not be chosen
 *   today: a deprecated filter still works, and a select with no option for its
 *   own value shows the wrong condition.
 * - The value control is keyed on what it is *about*, so changing the field or
 *   the condition throws away what was half-typed into the old one.
 */
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { Field, ViewFilterTypeName } from '@/api/types'
import type { FieldContext } from '../fields/behavior/behavior'
import { VIEW_FILTER_TYPES, fieldIsCompatible, type ViewFilterTypeDef } from '../filters'
import { filterFor, filterTypesFor } from '../filters/inputs'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import { FilterValue, type FilterValueSource } from './filter-value'

/**
 * The leading cell of a row: "where", the connective, or the connective again.
 *
 * Offered on the *second* row, because that is the first row where it decides
 * anything, and shown as text after that -- so it is stated without being asked
 * three times. Shared with the group rows beside these, which need the same
 * cell for the same reason: a group is one more thing combined by the
 * connective, and would otherwise carry a second copy of this rule.
 */
export function ConnectiveCell({
  position,
  connective,
  onConnective,
}: {
  position: number
  connective: 'AND' | 'OR'
  onConnective: (next: 'AND' | 'OR') => void
}) {
  const { t } = useTranslation()

  return (
    <div className="w-14 shrink-0 pt-1.5 text-xs text-muted-foreground">
      {position === 0 ? (
        t('view.filterWhere')
      ) : position === 1 ? (
        <NativeSelect
          size="sm"
          className="h-7 w-full px-1 pr-6 text-xs"
          aria-label={t('view.filterJoin')}
          value={connective}
          onChange={(event) => {
            onConnective(event.target.value === 'OR' ? 'OR' : 'AND')
          }}
        >
          <NativeSelectOption value="AND">{t('view.filterAnd')}</NativeSelectOption>
          <NativeSelectOption value="OR">{t('view.filterOr')}</NativeSelectOption>
        </NativeSelect>
      ) : (
        t(connective === 'OR' ? 'view.filterOr' : 'view.filterAnd')
      )}
    </div>
  )
}

/**
 * What a stored condition is, in each of the three places that store one.
 *
 * The id is a number or a string because the three disagree: a view's filters
 * and a form question's conditions are rows with ids the database hands out,
 * while a colour's conditions live inside one JSON column and are named by
 * whoever wrote them. Nothing here reads the id except as an identity, so the
 * difference stops at this line.
 */
export interface Condition extends FilterValueSource {
  id: number | string
  /**
   * The column and the comparison, either of which may be missing.
   *
   * Only on a colour's conditions, whose serializer allows null on both -- see
   * `FilterableCondition`. A row naming neither is drawn as one whose field is
   * gone, which is what it is.
   */
  field: number | null
  type: string | null
  /** The group it nests in, which this row never sets and must never lose. */
  group?: number | string | null
}

export interface ConditionChanges {
  field?: number
  type?: ViewFilterTypeName
  value?: string
}

export function ConditionRow({
  filter,
  position,
  connective,
  fields,
  context,
  onConnective,
  onChange,
  onRemove,
}: {
  filter: Condition
  position: number
  connective: 'AND' | 'OR'
  fields: readonly Field[]
  context: FieldContext
  onConnective: (next: 'AND' | 'OR') => void
  onChange: (changes: ConditionChanges) => void
  onRemove: () => void
}) {
  const { t } = useTranslation()
  const field = fields.find((candidate) => candidate.id === filter.field)
  const type = filter.type as ViewFilterTypeName
  const definition = VIEW_FILTER_TYPES[type] as ViewFilterTypeDef | undefined

  // A field the filter names but the list does not hold -- one that has been
  // deleted, or become unfilterable -- leaves the row unreadable rather than
  // guessed at. Removing it is still offered, which is the way out.
  if (field === undefined) {
    return (
      <li className="flex items-center gap-2 text-sm text-muted-foreground">
        <span className="w-14 shrink-0 text-xs">{t('view.filterWhere')}</span>
        <span className="flex-1">{t('view.filterNoFields')}</span>
        <RemoveButton name="" onRemove={onRemove} />
      </li>
    )
  }

  const choices = filterTypesFor(field)
  // The stored condition is offered even when it would not be chosen today: a
  // deprecated filter still works, and a select with no option for its own
  // value shows the wrong condition.
  const listed =
    definition !== undefined && !choices.includes(definition)
      ? [definition, ...choices]
      : choices

  return (
    <li className="flex items-start gap-2">
      <ConnectiveCell
        position={position}
        connective={connective}
        onConnective={onConnective}
      />

      <NativeSelect
        size="sm"
        className="w-32"
        aria-label={t('view.filterField', { position: position + 1 })}
        value={field.id}
        onChange={(event) => {
          const next = fields.find((c) => c.id === Number(event.target.value))
          if (next === undefined) return
          // The condition moves with the field, and only when it has to: a
          // `contains` that is valid on both stays, and one that is not is
          // replaced by the first the new field accepts. The value goes back to
          // that condition's default, because what a date filter holds means
          // nothing to a text one.
          const keep =
            definition !== undefined && fieldIsCompatible(definition, next)
              ? definition.type
              : filterTypesFor(next)[0]?.type
          if (keep === undefined) return
          onChange(filterFor(next, keep))
        }}
      >
        {fields.map((candidate) => (
          <NativeSelectOption key={candidate.id} value={candidate.id}>
            {candidate.name}
          </NativeSelectOption>
        ))}
      </NativeSelect>

      <NativeSelect
        size="sm"
        className="w-40"
        aria-label={t('view.filterType', { position: position + 1 })}
        value={type}
        onChange={(event) => {
          const next = event.target.value as ViewFilterTypeName
          // The field goes too, harmlessly: it is what it already was, and one
          // builder for all three paths is worth one redundant field id.
          onChange(filterFor(field, next))
        }}
      >
        {listed.map((choice) => (
          <NativeSelectOption key={choice.type} value={choice.type}>
            {t(`viewFilter.${choice.type}`)}
          </NativeSelectOption>
        ))}
      </NativeSelect>

      <FilterValue
        // Keyed on what the control is *about*, not just which filter it
        // belongs to. Changing the field or the condition replaces the value
        // with that condition's default, and a control that survived would
        // still be holding what was half-typed into the old one -- and would
        // write it back a moment later, onto a filter that is now about
        // something else.
        key={`${filter.id}-${filter.field}-${type}`}
        filter={filter}
        field={field}
        type={type}
        context={context}
        onChange={(value) => {
          onChange({ value })
        }}
      />

      <RemoveButton name={field.name} onRemove={onRemove} />
    </li>
  )
}

function RemoveButton({ name, onRemove }: { name: string; onRemove: () => void }) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      aria-label={t('view.filterRemove', { field: name })}
      onClick={onRemove}
      className="mt-1 rounded p-1 text-muted-foreground hover:bg-muted"
    >
      <X className="size-3.5" />
    </button>
  )
}
